import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {AvoidAreas, CreateAreaReworkPayload, IRoute, IRoutesState, IVehicleInfo, Route, Waypoint} from "@types";
import {IStop} from "../../components/RegisterRoute";
import {CreateRouteCostPayload} from "../../types/costSimulator";
import {CreateAreaPayload} from "../../types/areas";
import {string} from "zod";

const initialState: IRoutesState = {
    origin: null,
    destination: null,
    routes: [],
    stops: [],
    routeName: "",
    routeCost: {} as CreateRouteCostPayload,
    accountId: "",
    routeRangeToleranceLimit: 0,
    avoidToll: false,
    areas: [],
    areasRework: [],
    areasCopyRework: [],
    vehicle: {} as IVehicleInfo,
    areasToAvoid: {} as AvoidAreas,
    durationWithStops: "0h00min",
    waypoint: {} as Waypoint,
    routeId: null,
    areasSelectedIds: [],
};

const defaultVehicle = {
    grossWeight: '',
    height: '',
    lenght: '',
    width: '',
    type: '',
    maxAxleLoad: '',
    numberOfAxles: '',
    trailerAxles: '',
    shippedHazardousGoods: '',
    pollutantClass: '',
    maxLoadWeight: 0,
    weightType: '',
};

export const RoutesSlice = createSlice({
    name: "Routes",
    initialState,
    reducers: {
        defineOrigin: (state, action: PayloadAction<IRoute | null>) => {
            state.origin = action.payload;
        },
        defineDestination: (state, action: PayloadAction<IRoute | null>) => {
            state.destination = action.payload;
        },
        defineRoutes: (state, action: PayloadAction<Route[]>) => {
            state.routes = action.payload;
        },
        defineStops: (state, action: PayloadAction<IStop[]>) => {
            state.stops = action.payload;
        },
        defineRouteName: (state, action: PayloadAction<string>) => {
            state.routeName = action.payload;
        },
        defineAccountId: (state, action: PayloadAction<string>) => {
            state.accountId = action.payload;
        },
        defineRouteDeviation: (state, action: PayloadAction<number>) => {
            state.routeRangeToleranceLimit = action.payload;
        },
        defineRouteCost: (state, action: PayloadAction<CreateRouteCostPayload>) => {
          state.routeCost = {
            ...state.routeCost,
            ...action.payload,
            id: action.payload.id ?? state.routeCost.id,
          };
        },
        defineAvoidToll: (state, action: PayloadAction<boolean>) => {
            state.avoidToll = action.payload;
        },
        defineAreas: (state, action: PayloadAction<CreateAreaReworkPayload[]>) => {
            state.areas = action.payload;
        },
        defineAreasRework: (state, action: PayloadAction<CreateAreaReworkPayload[]>) => {
          state.areasRework = action.payload;
        },
        defineCopyAreasRework: (state, action: PayloadAction<CreateAreaReworkPayload[]>) => {
          state.areasCopyRework = action.payload;
        },
        defineVehicleInfo: (state, action: PayloadAction<IVehicleInfo>) => {
            state.vehicle = action.payload;
        },
        defineVehicleInfoDefault: (state) => {
            state.vehicle = defaultVehicle;
        },
        defineAvoidAreas: (state, action: PayloadAction<AvoidAreas>) => {
            state.areasToAvoid = action.payload;
        },
        defineDurationWithStops:(state,action:PayloadAction<string>)=>{
            state.durationWithStops = action.payload;
        },
        defineWaypoint: (state, action: PayloadAction<Waypoint | null>) => {
            state.waypoint = action.payload ?? ({} as Waypoint);
        },
        defineAreasSelectedIds: (state, action: PayloadAction<string[]>) => {
            state.areasSelectedIds = action.payload;
        },
        defineDistance: (state, action: PayloadAction<string | undefined>) => {
            state.distance = action?.payload;
        },
        defineRouteId: (state, action: PayloadAction<string | null>) => {
            state.routeId = action.payload;
        },
    },
});

export const {
    defineOrigin,
    defineDestination,
    defineRoutes,
    defineStops,
    defineRouteName,
    defineAccountId,
    defineRouteDeviation,
    defineRouteCost,
    defineAvoidToll,
    defineAreas,
    defineAreasRework,
    defineVehicleInfo,
    defineVehicleInfoDefault,
    defineAvoidAreas,
    defineDurationWithStops,
    defineWaypoint,
    defineAreasSelectedIds,
    defineDistance,
    defineRouteId,
    defineCopyAreasRework,
} = RoutesSlice.actions;

export default RoutesSlice.reducer;

